import { MusicIcon } from '../components/icons/MusicIcon';
import { RoomName } from '../components/icons/RoomName';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../stores/authStore';
import { useSocketStore } from '../stores/socketStore';
import { useAutoRooms } from '../hooks/useAutoRooms';
import { usePremium } from '../hooks/usePremium';
import AuthModal from '../components/AuthModal';
import HomeLive from '../components/home-live/HomeLive';
import LinkAccountsModal from '../components/LinkAccountsModal';
import { SceneThemeArtwork } from '../components/scene/SceneThemeArtwork';
import { parseRoomInvitation } from '../services/roomInvitation';
import { Users, Trophy, ArrowRight, Play, Crown } from 'lucide-react';

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, player, error: authError } = useAuthStore();
  const { socket, isConnected, publicRooms, fetchPublicRooms, joinRoom, authAsGuest, currentRoom, resetLeftRoom, isJoining, sessionError, clearIntent, retryJoin } = useSocketStore();
  const { themes, isLoading: themesLoading, joinAutoRoom } = useAutoRooms();
  const { isPremium } = usePremium();
  
  // Reset le flag hasLeftRoom quand on arrive sur la page d'accueil
  useEffect(() => {
    resetLeftRoom();
  }, [resetLeftRoom]);
  
  // Naviguer vers la room quand on rejoint une auto-room
  useEffect(() => {
    if (currentRoom) {
      navigate(`/room/${currentRoom.code}`);
    }
  }, [currentRoom, navigate]);
  
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkedGames, setLinkedGames] = useState<any[]>([]);
  const [linkBenefits, setLinkBenefits] = useState<string[]>([]);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const [roomCode, setRoomCode] = useState('');
  const [guestName, setGuestName] = useState('');
  const [discoveryMode, setDiscoveryMode] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<any>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const login = params.get('auth') === 'login';
    const invitation = parseRoomInvitation(location.search);
    if (login) {
      setAuthModalMode('login');
      setShowAuthModal(true);
      params.delete('auth');
    }
    if (invitation) {
      joinRoom(invitation);
      if (!isAuthenticated && !login) setShowGuestModal(true);
      params.delete('room');
    }
    if (login || invitation) navigate({ pathname: '/', search: params.toString() ? `?${params}` : '' }, { replace: true });
  }, [location.search, navigate, joinRoom, isAuthenticated]);

  // Fetch current weekly event
  useEffect(() => {
    if (socket && isConnected) {
      socket.emit('events:current');
      const handler = (data: any) => setCurrentEvent(data.current);
      socket.on('events:current', handler);
      return () => { socket.off('events:current', handler); };
    }
  }, [socket, isConnected]);
  
  const handleOpenLogin = () => {
    setAuthModalMode('login');
    setShowAuthModal(true);
  };
  
  const handleOpenRegister = () => {
    setAuthModalMode('register');
    setShowAuthModal(true);
  };
  
  // Vérifier si l'email a des comptes sur d'autres jeux
  const checkLinkedAccounts = async (email: string) => {
    try {
      const response = await fetch(`/auth/check-linked-accounts?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.success && data.hasLinkedAccounts) {
        setLinkedGames(data.linkedGames);
        setLinkBenefits(data.benefits);
        setShowLinkModal(true);
      }
    } catch (error) {
      console.error('Erreur vérification comptes liés:', error);
    }
  };
  
  // Lier un compte avec un autre jeu
  const handleLinkAccount = async (gameId: string): Promise<boolean> => {
    try {
      // Récupérer le token depuis le store Zustand
      const authData = localStorage.getItem('blindtest-auth');
      let token = null;
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          token = parsed.state?.token;
        } catch (e) {
          console.error('Erreur parsing auth data:', e);
        }
      }
      
      if (!token) {
        console.error('Token non trouvé');
        return false;
      }
      
      const response = await fetch('/auth/link-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ targetGameId: gameId })
      });
      
      const data = await response.json();
      return data.success === true;
    } catch (error) {
      console.error('Erreur liaison compte:', error);
      return false;
    }
  };
  
  const handleGuestLogin = () => {
    if (sessionError && useSocketStore.getState().lastIntent) { retryJoin(); return; }
    if (guestName.trim().length >= 2 && guestName.trim().length <= 20) authAsGuest(guestName.trim());
  };
  const handleJoinAutoRoom = (themeId: string) => {
    joinAutoRoom(themeId, discoveryMode || !isAuthenticated || player?.type === 'guest');
    if (!isAuthenticated) setShowGuestModal(true);
  };
  const handleClickPublicRoom = (code: string) => {
    joinRoom(code);
    if (!isAuthenticated) setShowGuestModal(true);
  };
  const handleJoinRoom = () => {
    if (roomCode.trim().length < 4) return;
    setShowJoinModal(false);
    handleClickPublicRoom(roomCode.trim().toUpperCase());
  };
  const closeGuest = () => {
    clearIntent(); setShowGuestModal(false);
  };
  const startDiscovery = () => {
    setDiscoveryMode(true);
    const themesSection = document.getElementById('quick-themes');
    themesSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    themesSection?.focus({ preventScroll: true });
  };

  return (
    <div className="max-w-6xl mx-auto">
      {sessionError && <div role="alert" className="card text-red-300 mb-4">{sessionError} <button className="btn-secondary ml-3" onClick={retryJoin}>Réessayer</button></div>}
      {authError && !showAuthModal && <p role="alert" className="card text-red-300 mb-4">{authError}</p>}
      {isJoining && <p role="status" className="text-center text-primary-300">Connexion à la partie…</p>}
      <section className="scene-hero">
        <div>
          <h1>Tu la connais.<br />Prouve-le.</h1>
          <p>Les premières notes. Le déclic. Le titre sur le bout de la langue. À vous de jouer.</p>
          <div className="scene-actions">
            <button className="scene-button scene-button-main" onClick={startDiscovery}><Play size={18} />Partie rapide</button>
            <button className="scene-button scene-button-outline" onClick={() => isAuthenticated && player?.type !== 'guest' ? navigate('/create') : handleOpenLogin()}><Users size={18} />Entre amis</button>
          </div>
          <div className="scene-notes"><span><MusicIcon name="note"/> 5 extraits</span><span><MusicIcon name="clock"/> Environ 2 minutes</span><span><MusicIcon name="check"/> Sans compte</span></div>
          {!isAuthenticated && <div className="scene-auth-links"><button onClick={handleOpenLogin}>Se connecter</button><button onClick={handleOpenRegister}>Créer un compte</button><button onClick={() => setShowJoinModal(true)}>Rejoindre avec un code</button></div>}
          {isAuthenticated && <button className="scene-inline-link scene-join" onClick={() => setShowJoinModal(true)}>Rejoindre avec un code</button>}
        </div>
        <div className="scene-hero-art scene-home-live-stage">
          <div className="scene-live-decor" aria-hidden="true">
          <div className="scene-sleeve scene-sleeve-back" aria-hidden="true"><b>Le son<br />du cinéma.</b><i /></div>
          <div className="scene-sleeve scene-sleeve-right" aria-hidden="true"><b>Monte<br />le son.</b></div>
          </div>
          <HomeLive suspended={showJoinModal || showGuestModal || showAuthModal || showLinkModal || isJoining} />
          <small>À chacun son refrain.</small>
        </div>
      </section>
      <section id="quick-themes" tabIndex={-1} className="scene-themes-section">
        <div className="scene-section-heading"><div><h2>Quel est ton univers ?</h2><p>{discoveryMode || !isAuthenticated || player?.type === 'guest' ? '5 extraits · sans compte. Choisis un thème puis ton pseudo.' : 'Choisis l’ambiance. On s’occupe de la musique.'}</p></div><button className="scene-inline-link" onClick={() => navigate('/games')}>Toutes les parties</button></div>
        <div className="scene-themes">
          {themesLoading ? <p role="status">Chargement des thèmes…</p> : themes.length ? themes.map(theme => <button className="scene-theme" key={theme.id} onClick={() => handleJoinAutoRoom(theme.id)}>
            <SceneThemeArtwork name={theme.name} themeId={theme.id} />
            <span className="scene-theme-info"><span><strong>{theme.name}</strong><small>{theme.playerCount > 0 ? `${theme.playerCount} joueurs` : theme.description || 'Rejoindre'}</small></span><span className="scene-theme-play"><Play size={13} /></span></span>
          </button>) : <p>Aucun thème disponible</p>}
        </div>
      </section>
      <section className="scene-below">
        {currentEvent && <div className="scene-feature"><Trophy size={34} /><div><h3>{currentEvent.title}</h3><p>{currentEvent.description}</p><p>XP x{currentEvent.xp_multiplier} · Termine dans {Math.max(0, Math.ceil((new Date(currentEvent.end_date).getTime() - Date.now()) / 86400000))}j</p><button className="scene-inline-link" onClick={() => navigate('/event')}>Classement de l’événement</button> <button className="scene-inline-link" onClick={() => navigate('/create')}>Participer</button></div></div>}
        <div className="scene-feature"><Users size={34} /><div><h3>Encore une dernière ?</h3><p>Invite tes amis. Choisissez ensemble la suite.</p><button className="scene-inline-link" onClick={() => isAuthenticated && player?.type !== 'guest' ? navigate('/create') : handleOpenLogin()}>Créer votre soirée</button></div></div>
        {!isPremium && <div className="scene-feature"><Crown size={34} /><div><h3>Plus de musique avec Premium</h3><p>Découvre les modes et avantages Premium.</p><button className="scene-inline-link" onClick={() => navigate('/premium')}>Découvrir Premium</button></div></div>}
      </section>
      <section className="scene-public"><div className="scene-section-heading"><div><h2>Parties en cours</h2><p>Rejoins une partie publique.</p></div><button className="scene-inline-link" onClick={fetchPublicRooms}>Actualiser</button></div>
        {publicRooms.length ? <div className="scene-room-grid">{publicRooms.map(room => <button key={room.code} className="scene-room" onClick={() => handleClickPublicRoom(room.code)}><strong><RoomName name={room.name} isAutoRoom={room.isAutoRoom}/></strong><span>{room.code} · {room.mode}</span><span>Par {room.hostName} · {room.playerCount}/{room.maxPlayers} joueurs</span></button>)}</div> : <p>Aucune partie publique en cours</p>}
      </section>

      {/* Modal Rejoindre */}
      {showJoinModal && (
        <Modal onClose={() => setShowJoinModal(false)}>
          <h2 className="font-display text-xl font-bold mb-4">Rejoindre une partie</h2>
          <input
            type="text"
            value={roomCode}
            onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
            placeholder="Code de la room (ex: ABC123)"
            className="input-field mb-4"
            maxLength={6}
            autoFocus
          />
          <button 
            onClick={handleJoinRoom}
            className="btn-primary w-full"
            disabled={roomCode.length < 4}
          >
            <ArrowRight className="w-5 h-5 inline mr-2" />
            Rejoindre
          </button>
        </Modal>
      )}
      
      {/* Modal Invité */}
      {showGuestModal && (
        <Modal onClose={closeGuest}>
          <h2 className="font-display text-xl font-bold mb-4">Jouer en invité</h2>
          <p className="text-dark-400 text-sm mb-4">
            Trouve le titre ou l’artiste. Cette partie invitée ne sauvegarde pas de progression.
          </p>
          {sessionError && <p role="alert" className="text-red-300 mb-3">{sessionError}</p>}
          <input
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Votre pseudo"
            className="input-field mb-4"
            maxLength={20}
            autoFocus
          />
          <button 
            onClick={handleGuestLogin}
            className="btn-primary w-full"
            disabled={isJoining || guestName.trim().length < 2 || guestName.trim().length > 20}
          >
            <Play className="w-5 h-5 inline mr-2" />
            {isJoining ? 'Connexion…' : 'Commencer'}
          </button>
          <button onClick={() => { setShowGuestModal(false); handleOpenLogin(); }} className="w-full mt-3 text-sm text-primary-300">Déjà un compte ? Se connecter</button>
        </Modal>
      )}
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => {
          setShowAuthModal(false);
          const state = useAuthStore.getState();
          if (!state.isAuthenticated || state.player?.type === 'guest') clearIntent();
        }}
        initialMode={authModalMode}
        onSuccess={checkLinkedAccounts}
      />
      
      {/* Link Accounts Modal */}
      <LinkAccountsModal
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        linkedGames={linkedGames}
        benefits={linkBenefits}
        onLinkAccount={handleLinkAccount}
      />
    </div>
  );
}

// Modal Component
function Modal({ 
  children, 
  onClose 
}: { 
  children: React.ReactNode; 
  onClose: () => void;
}) {
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div 
        role="dialog" aria-modal="true" className="card max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.div>
    </div>
  );
}

